import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Button, FullPageStatus, showToast } from "@vv/ui";
import { fetchTeamMembers, useTeamsDispatch, useTeamsSelector } from "@vv/teams-frontend";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { AssignModal } from "../components/AssignModal";
import { AttachmentsSection } from "../components/AttachmentsSection";
import { CommentsSection } from "../components/CommentsSection";
import { CompleteModal } from "../components/CompleteModal";
import { PriorityChip } from "../components/PriorityChip";
import { RejectModal } from "../components/RejectModal";
import { RequestReworkModal } from "../components/RequestReworkModal";
import { StatusChip } from "../components/StatusChip";
import { RequestStatus, VALIDATION_TYPE_LABELS } from "../domain/request.types";
import { canPerformAction } from "../lib/permissions";
import { useRequestsDispatch, useRequestsSelector } from "../state/hooks";
import {
  acceptRequest,
  approveReworkRequest,
  completeRequest,
  fetchRequest,
  rejectRequest,
  rejectReworkRequest,
  requestReworkAction,
  startRequest,
} from "../state/requests.thunks";

export interface RequestDetailPageProps {
  currentUserId: string;
  isOwner: boolean;
}

type ModalKind = "accept" | "reject" | "complete" | "requestRework" | "approveRework" | null;

export function RequestDetailPage({ currentUserId, isOwner }: RequestDetailPageProps) {
  const { requestId } = useParams<{ requestId: string }>();
  const dispatch = useRequestsDispatch();
  const teamsDispatch = useTeamsDispatch();

  const request = useRequestsSelector((state) =>
    state.requests.requests.find((r) => r.id === requestId),
  );
  const status = useRequestsSelector((state) => state.requests.status);
  const teams = useTeamsSelector((state) => state.teams.teams);
  const members = useTeamsSelector((state) =>
    request ? state.teams.members[request.teamId] : undefined,
  );

  const [openModal, setOpenModal] = useState<ModalKind>(null);
  const [actionPending, setActionPending] = useState(false);

  useEffect(() => {
    if (requestId) dispatch(fetchRequest(requestId));
  }, [dispatch, requestId]);

  useEffect(() => {
    if (request) teamsDispatch(fetchTeamMembers(request.teamId));
  }, [teamsDispatch, request]);

  const myRoles = useMemo(
    () =>
      (members ?? [])
        .filter((member) => member.userId === currentUserId)
        .map((member) => member.role),
    [members, currentUserId],
  );

  const teamName = useMemo(
    () => teams.find((team) => team.id === request?.teamId)?.name ?? "—",
    [teams, request],
  );

  if (!request) {
    if (status === "failed") {
      return <FullPageStatus variant="error" title="Could not load request" />;
    }
    return <FullPageStatus variant="loading" title="Loading request..." />;
  }

  const isOriginalRequestor = isOwner || request.requestorId === currentUserId;
  const isAssignedEngineer = isOwner || request.assignedEngineerIds.includes(currentUserId);

  const canAccept =
    request.status === RequestStatus.Submitted && canPerformAction("accept", isOwner, myRoles);
  const canReject =
    request.status === RequestStatus.Submitted && canPerformAction("reject", isOwner, myRoles);
  const canStart =
    request.status === RequestStatus.Assigned &&
    canPerformAction("start", isOwner, myRoles) &&
    isAssignedEngineer;
  const canComplete =
    request.status === RequestStatus.InProgress &&
    canPerformAction("complete", isOwner, myRoles) &&
    isAssignedEngineer;
  const canRequestRework =
    request.status === RequestStatus.Completed &&
    canPerformAction("requestRework", isOwner, myRoles) &&
    isOriginalRequestor;
  const canApproveRework =
    request.status === RequestStatus.ReworkRequired &&
    canPerformAction("approveRework", isOwner, myRoles);
  const canRejectRework =
    request.status === RequestStatus.ReworkRequired &&
    canPerformAction("rejectRework", isOwner, myRoles);

  async function runAction(run: () => Promise<unknown>, successMessage: string) {
    setActionPending(true);
    try {
      await run();
      showToast(successMessage, "success");
      setOpenModal(null);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Action failed", "error");
      throw err;
    } finally {
      setActionPending(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 960 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {request.displayId}
        </Typography>
        <StatusChip status={request.status} />
        <PriorityChip priority={request.priority} />
      </Stack>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {request.title}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        <InfoItem label="Team" value={teamName} />
        <InfoItem label="Validation type" value={VALIDATION_TYPE_LABELS[request.validationType]} />
        <InfoItem
          label="Expected completion"
          value={request.expectedCompletionDate ?? "Not set yet"}
        />
        <InfoItem label="Actual completion" value={request.actualCompletionDate ?? "—"} />
        <InfoItem
          label="Assigned engineer(s)"
          value={request.assignedEngineerIds.length ? request.assignedEngineerIds.join(", ") : "—"}
        />
        <InfoItem label="Validation result" value={request.validationResult ?? "—"} />
      </Box>

      {request.description ? (
        <Typography variant="body2" sx={{ mb: 3 }}>
          {request.description}
        </Typography>
      ) : null}

      {request.rejectionReason ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Rejected: {request.rejectionReason}
        </Alert>
      ) : null}
      {request.reworkReason ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Rework requested: {request.reworkReason}
        </Alert>
      ) : null}
      {request.extensionReason ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Completed late - extension reason: {request.extensionReason}
        </Alert>
      ) : null}

      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
        {canAccept ? (
          <Button variant="contained" onClick={() => setOpenModal("accept")}>
            Accept
          </Button>
        ) : null}
        {canReject ? (
          <Button variant="outlined" color="error" onClick={() => setOpenModal("reject")}>
            Reject
          </Button>
        ) : null}
        {canStart ? (
          <Button
            variant="contained"
            loading={actionPending}
            onClick={() =>
              runAction(() => dispatch(startRequest(request.id)).unwrap(), "Validation started")
            }
          >
            Start validation
          </Button>
        ) : null}
        {canComplete ? (
          <Button variant="contained" color="success" onClick={() => setOpenModal("complete")}>
            Complete validation
          </Button>
        ) : null}
        {canRequestRework ? (
          <Button variant="outlined" color="warning" onClick={() => setOpenModal("requestRework")}>
            Request rework
          </Button>
        ) : null}
        {canApproveRework ? (
          <Button variant="contained" onClick={() => setOpenModal("approveRework")}>
            Approve rework
          </Button>
        ) : null}
        {canRejectRework ? (
          <Button
            variant="outlined"
            color="error"
            loading={actionPending}
            onClick={() =>
              runAction(
                () => dispatch(rejectReworkRequest(request.id)).unwrap(),
                "Rework request denied",
              )
            }
          >
            Reject rework
          </Button>
        ) : null}
      </Stack>

      <Divider sx={{ mb: 3 }} />

      <AttachmentsSection requestId={request.id} />

      <Divider sx={{ mb: 3 }} />

      <CommentsSection requestId={request.id} />

      <RejectModal
        open={openModal === "reject"}
        onClose={() => setOpenModal(null)}
        onConfirm={(rejectionReason) =>
          runAction(
            () =>
              dispatch(rejectRequest({ id: request.id, payload: { rejectionReason } })).unwrap(),
            "Request rejected",
          )
        }
      />
      <AssignModal
        open={openModal === "accept" || openModal === "approveRework"}
        onClose={() => setOpenModal(null)}
        title={openModal === "approveRework" ? "Approve rework request" : "Accept request"}
        confirmLabel={openModal === "approveRework" ? "Approve" : "Accept"}
        onConfirm={(payload) =>
          runAction(
            () =>
              (openModal === "approveRework"
                ? dispatch(approveReworkRequest({ id: request.id, payload }))
                : dispatch(acceptRequest({ id: request.id, payload }))
              ).unwrap(),
            openModal === "approveRework" ? "Rework approved" : "Request accepted",
          )
        }
      />
      <CompleteModal
        open={openModal === "complete"}
        onClose={() => setOpenModal(null)}
        expectedCompletionDate={request.expectedCompletionDate}
        onConfirm={(payload) =>
          runAction(
            () => dispatch(completeRequest({ id: request.id, payload })).unwrap(),
            "Validation completed",
          )
        }
      />
      <RequestReworkModal
        open={openModal === "requestRework"}
        onClose={() => setOpenModal(null)}
        onConfirm={(reworkReason) =>
          runAction(
            () =>
              dispatch(requestReworkAction({ id: request.id, payload: { reworkReason } })).unwrap(),
            "Rework requested",
          )
        }
      />
    </Box>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}
